import { defineStore } from 'pinia'
import { ref } from 'vue'
import { SinchVideoService } from '../services/sinch-video'
import { collection, addDoc, updateDoc, doc, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '../plugins/firebase'

export const useVideoConferenceStore = defineStore('videoConference', () => {
  const sinchVideo = ref(new SinchVideoService({
    applicationKey: import.meta.env.VITE_SINCH_APP_KEY,
    applicationSecret: import.meta.env.VITE_SINCH_APP_SECRET
  }))
  
  const conference = ref(null)
  const participants = ref([])
  const rooms = ref([])
  const currentRoom = ref(null)
  const loading = ref(false)
  const error = ref(null)

  // Initialize conference
  const initializeConference = async (conferenceId) => {
    try {
      loading.value = true

      // Get conference details
      const conferenceDoc = await doc(db, 'conferences', conferenceId).get()
      conference.value = {
        id: conferenceDoc.id,
        ...conferenceDoc.data()
      }

      // Initialize Sinch client
      await sinchVideo.value.initialize(auth.currentUser.uid)

      // Set up event listeners
      setupEventListeners()

      // Set up real-time listeners
      initializeListeners(conferenceId)

      return conference.value
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // Set up Sinch event listeners
  const setupEventListeners = () => {
    sinchVideo.value.on('participantJoined', (participant) => {
      participants.value.push(participant)
    })

    sinchVideo.value.on('participantLeft', (participant) => {
      participants.value = participants.value.filter(p => p.id !== participant.id)
    })

    sinchVideo.value.on('trackAdded', (participant, track) => {
      // Handle new media track
    })

    sinchVideo.value.on('trackRemoved', (participant, track) => {
      // Handle removed media track
    })
  }

  // Set up Firestore listeners
  const initializeListeners = (conferenceId) => {
    // Listen for room changes
    const roomsQuery = query(
      collection(db, 'conference_rooms'),
      where('conferenceId', '==', conferenceId)
    )
    
    return onSnapshot(roomsQuery, (snapshot) => {
      rooms.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    })
  }

  // Media controls
  const toggleAudio = async (enabled) => {
    try {
      await sinchVideo.value.toggleAudio(enabled)
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  const toggleVideo = async (enabled) => {
    try {
      await sinchVideo.value.toggleVideo(enabled)
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  const toggleScreenShare = async () => {
    try {
      if (sinchVideo.value.isScreenSharing) {
        await sinchVideo.value.stopScreenShare()
      } else {
        await sinchVideo.value.startScreenShare()
      }
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  // Room management
  const createRoom = async (roomData) => {
    try {
      loading.value = true
      
      // Create Sinch room
      const sinchRoom = await sinchVideo.value.createRoom({
        name: roomData.name,
        maxParticipants: roomData.maxParticipants,
        moderators: roomData.moderators,
        audioEnabled: roomData.audioEnabled,
        videoEnabled: roomData.videoEnabled
      })

      // Store room details in Firestore
      await addDoc(collection(db, 'conference_rooms'), {
        ...roomData,
        sinchRoomId: sinchRoom.id,
        participants: [],
        createdAt: new Date()
      })
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  const joinRoom = async (roomId) => {
    try {
      loading.value = true
      
      // Get room details
      const roomDoc = await doc(db, 'conference_rooms', roomId).get()
      const room = roomDoc.data()

      // Join Sinch room
      await sinchVideo.value.joinRoom(room.sinchRoomId)

      // Update Firestore
      await updateDoc(roomDoc.ref, {
        participants: admin.firestore.FieldValue.arrayUnion(auth.currentUser.uid)
      })

      currentRoom.value = {
        id: roomDoc.id,
        ...room
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  const leaveRoom = async () => {
    try {
      loading.value = true
      
      if (currentRoom.value) {
        // Leave Sinch room
        await sinchVideo.value.leaveRoom()

        // Update Firestore
        await updateDoc(doc(db, 'conference_rooms', currentRoom.value.id), {
          participants: admin.firestore.FieldValue.arrayRemove(auth.currentUser.uid)
        })

        currentRoom.value = null
      }
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // Clean up
  const cleanup = () => {
    sinchVideo.value.cleanup()
  }

  return {
    conference,
    participants,
    rooms,
    currentRoom,
    loading,
    error,
    initializeConference,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    createRoom,
    joinRoom,
    leaveRoom,
    cleanup
  }
})